import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import type { BlogPost } from '@shared/blog';

interface Props {
  post: BlogPost;
  index?: number;
}

const CATEGORY_ACCENT: Record<string, string> = {
  Beginner: '#10b981',
  Brand: '#3b82f6',
  Comparison: '#8b5cf6',
  Lifestyle: '#f59e0b',
  Health: '#ef4444',
  India: '#06b6d4',
};

export default function BlogCard({ post, index = 0 }: Props) {
  const { isDark } = useTheme();
  const accent = CATEGORY_ACCENT[post.category] ?? '#10b981';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05, duration: 0.4 }}
      className={`group rounded-2xl border overflow-hidden transition-all duration-300 hover:-translate-y-1 ${
        isDark
          ? 'bg-gray-900 border-gray-800 hover:shadow-xl hover:shadow-black/30'
          : 'bg-white border-gray-100 shadow-sm hover:shadow-xl hover:shadow-gray-200/60'
      }`}
    >
      <Link to={`/blog/${post.slug}`} className="block">
        {/* Image / placeholder container */}
        <div
          className="aspect-[16/9] flex items-center justify-center relative overflow-hidden"
          style={{ background: `linear-gradient(145deg, ${accent}10, ${accent}25, ${accent}10)` }}
        >
          <img
            src={post.image}
            alt={`${post.title} — ZeroSmoke blog`}
            className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
            loading="lazy"
            onError={(e) => {
              // graceful fallback when /images/blog/placeholder.jpg is not yet present
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />

          {/* Category chip */}
          <div className="absolute top-3 left-3">
            <span
              className="px-2.5 py-1 text-[10px] font-bold rounded-md uppercase tracking-wider shadow-sm text-white"
              style={{ backgroundColor: accent }}
            >
              {post.category}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-5">
          <h3
            className={`font-bold text-lg leading-snug group-hover:text-emerald-500 transition-colors line-clamp-2 mb-2 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}
          >
            {post.title}
          </h3>

          <p className={`text-sm line-clamp-2 mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {post.excerpt}
          </p>

          <div
            className={`flex items-center gap-1.5 text-xs ${
              isDark ? 'text-gray-500' : 'text-gray-500'
            }`}
          >
            <Clock size={12} />
            <span>{post.readTime} min read</span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
